import { useEffect } from 'react';
import { PLACEHOLDER_SRC } from '../lib/photoFiles';
import { usePhotoGestures } from '../hooks/usePhotoGestures';
import { usePhotoSrc } from '../hooks/usePhotoSrc';
import type { Photo } from '../types';

type Props = {
  photos: Photo[];
  index: number;
  onClose: () => void;
  onIndex: (index: number) => void;
};

export function Lightbox({ photos, index, onClose, onIndex }: Props) {
  const photo = photos[index];
  const stored = photo?.displayUrl ?? photo?.thumbUrl ?? null;
  const fetched = usePhotoSrc(!photo || stored ? '' : photo.id);
  const src = stored ?? fetched.src ?? PLACEHOLDER_SRC;
  const n = photos.length;
  const { view, stageProps } = usePhotoGestures(index, n, onIndex);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex((index + 1) % n);
      if (e.key === 'ArrowLeft') onIndex((index - 1 + n) % n);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [index, n, onClose, onIndex]);

  if (!photo) return null;

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={photo.filename}>
      <div className="lightbox-bar">
        <span>
          {index + 1} / {photos.length} · {photo.filename}
        </span>
        <button type="button" className="btn ghost" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="lightbox-stage" {...stageProps}>
        <div
          className="lightbox-zoom"
          style={{
            transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`,
          }}
        >
          <img
            src={src}
            alt={photo.caption || photo.filename}
            className={src === PLACEHOLDER_SRC ? 'is-placeholder' : undefined}
            draggable={false}
          />
        </div>
      </div>
      <div className="btn-row">
        <button type="button" className="btn" onClick={() => onIndex((index - 1 + n) % n)}>
          Previous
        </button>
        <button type="button" className="btn" onClick={() => onIndex((index + 1) % n)}>
          Next
        </button>
      </div>
    </div>
  );
}
